<template>
  <div class="flex flex-col gap-4">
    <div
      ref="categoriesGrid"
      class="categories grid gap-2 overflow-hidden"
      :class="{ 'search-bar-expanded': searchBarExpanded }"
      :style="{ 'max-height': dynamicMaxHeight }"
    >
      <transition-group name="categories-list">
        <div
          ref="searchContainer"
          key="searchBar"
          :style="searchContainerStyle"
          class="flex gap-2 transition-all duration-300 ease"
        >
          <search-bar
            ref="searchCategory"
            v-model="search"
            :expanded="searchBarExpanded"
            class="flex-1"
            @update:expanded="value => toggleSearchBar(value)"
          />
          <div
            v-if="!searchBarExpanded"
            :class="{
              'bg-v-300': selectedCategoryId === 'promotions',
              'bg-n-700': selectedCategoryId !== 'promotions'
            }"
            class="flex flex-1 items-center justify-center text-n-0 text-center rounded font-body text-sm cursor-pointer h-12 sm:h-14"
            @click="selectCategory('promotions')"
          >
            <l-icon name="discount" size="medium" />
          </div>
        </div>
        <template v-if="!searchBarExpanded">
          <category
            v-for="category in categoryList"
            :key="category.id"
            :category="category"
            :selected="selectedCategoryId === category.id"
            class="w-full"
            @selected="selectCategory(category.id)"
          />
        </template>
      </transition-group>
    </div>
    <div v-if="showToggle && !searchBarExpanded" class="flex items-center h-9 relative">
      <div class="flex-1 border border-n-700 justify-center"></div>
      <div
        class="absolute left-1/2 transform -translate-x-1/2 bg-n-700 p-2 rounded-full cursor-pointer"
        @click="toggleExpand"
      >
        <l-icon
          name="bracket-down"
          class="w-6 h-6 text-n-0 transition"
          :class="{ 'rotate-180': expanded }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useElementSize, useEventListener, useResizeObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute } from 'vue-router'

import { sleep } from '@last/core'
import { LIcon } from '@last/core-ui/paprika'

import SearchBar from '@/components/SearchBar.vue'
import Category from '@/modules/ordering/Category.vue'
import { useCatalogStore } from '@/store/catalog'
import type { CatalogCategory } from '@/types'
import { isMobile } from '@/utils/isMobile'

const props = defineProps<{
  catalogId: string | null
}>()

const emit = defineEmits<{
  categorySelected: [id: string]
  'update:search': [value: string]
  searchBarExpanded: [value: boolean]
}>()

const categoriesGridEl = useTemplateRef('categoriesGrid')
const searchCategoryEl = useTemplateRef('searchCategory')

const route = useRoute()
const catalogStore = useCatalogStore()
const { catalogs, categories } = storeToRefs(catalogStore)

const selectedCategoryId = ref<string | null>(null)
const disableCategorySelection = ref<boolean>(false)

const search = ref('')
const searchBarExpanded = ref(false)
const expanded = ref<boolean>(false)
const showToggle = ref<boolean>(false)
const dynamicMaxHeight = ref<string>('7.5rem')
const searchContainerStyle = ref({})

onMounted(() => {
  preselectCategory()
  checkOverflow()
  calculateSize()
})

const categoryIds = computed(() => {
  if (!props.catalogId) return []
  return catalogs.value[props.catalogId]?.categories || []
})

const categoryList = computed(() => {
  return categoryIds.value.map((id: string) => ({
    id,
    name: ellipsis(getCategories.value[id]?.name || '', 20)
  }))
})

const getCategories = computed(() => {
  if (!props.catalogId) return {}
  const catalog = catalogs.value[props.catalogId]
  if (!catalog) return {}
  return catalog.categories.reduce(
    (res, categoryId) => {
      res[categoryId] = categories.value[categoryId]
      return res
    },
    {} as Record<string, CatalogCategory>
  )
})

watch(
  categoryIds,
  () => {
    preselectCategory()
  },
  { immediate: true }
)

watch(
  categoryList,
  () => {
    calculateSize()
  },
  { deep: true }
)

watch(searchBarExpanded, value => {
  if (selectedCategoryId.value === 'promotions') {
    selectCategory(categoryIds.value[0])
  }
  emit('searchBarExpanded', value)
  calculateSize()
})

watch(search, () => {
  emit('update:search', search.value)
})

useEventListener('resize', () => {
  checkOverflow()
  calculateSize()
})

useResizeObserver(categoriesGridEl, () => {
  calculateSize()
})

let measureRafId: number | null = null
function calculateSize() {
  if (measureRafId != null) return
  // rAF-based measurement scheduler to avoid repeated sync layout reads
  measureRafId = requestAnimationFrame(() => {
    measureRafId = null
    const grid = categoriesGridEl.value as HTMLElement | undefined
    if (!grid) return
    const scrollH = grid.scrollHeight

    if (expanded.value) {
      dynamicMaxHeight.value = `${scrollH}px`
    } else {
      dynamicMaxHeight.value = isMobile.value ? '6.5rem' : '7.5rem'
    }
  })
}

function checkOverflow() {
  const grid = categoriesGridEl.value
  showToggle.value = (grid?.scrollHeight ?? 0) > (grid?.clientHeight ?? 0)
}

function preselectCategory() {
  if (!selectedCategoryId.value || !getCategories.value[selectedCategoryId.value]) {
    const param = route.query.selectedCategoryId as string
    selectedCategoryId.value =
      param && categoryIds.value.includes(param) ? param : categoryIds.value[0]
    emit('categorySelected', selectedCategoryId.value)
  }
}

function selectCategory(id: string): void {
  if (disableCategorySelection.value) return
  selectedCategoryId.value = id
  emit('categorySelected', id)
}

function ellipsis(name: string, maxLength: number): string {
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name
}

function toggleExpand() {
  expanded.value = !expanded.value
  calculateSize()
}

async function toggleSearchBar(value: boolean) {
  if (value === searchBarExpanded.value) return
  if (searchBarExpanded.value) {
    searchContainerStyle.value = {}
    searchBarExpanded.value = value
    calculateSize()
  } else {
    const { width: initialWidth } = useElementSize(searchCategoryEl.value?.$el)
    if (searchCategoryEl.value) {
      searchContainerStyle.value = { width: `${initialWidth.value}px` }
    }
    searchBarExpanded.value = value
    await sleep(250)
    if (searchCategoryEl.value) {
      searchContainerStyle.value = { width: `100%`, 'grid-column': '1 / -1' }
    }
    calculateSize()
  }
}
</script>

<style scoped>
.categories {
  grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
  transition: all 0.2s;
}

@media screen and (max-width: 640px) {
  .categories {
    grid-template-columns: repeat(auto-fill, minmax(7rem, 1fr));
  }
}

.categories-list-enter-from {
  opacity: 0;
  transform: scale(0.6);
}

.categories-list-enter-active {
  transition: all 0.2s ease;
}

.categories-list-leave-to {
  opacity: 0;
  transform: scale(0.6);
}

.categories-list-leave-active {
  transition: all 0.2s ease;
}

.categories-list-move {
  transition: all 0.2s ease;
}
</style>
